import React, { useCallback } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import { useModalMinimize } from 'contexts/ModalMinimizeContext';
import type { Room, Property, RoomCategory } from 'api/types';

// ============================================================================
// TYPE DEFINITIONS
// ============================================================================

export interface RoomViewModalProps {
  isOpen: boolean;
  room: Room | null;
  propertyLookup: Record<string, Property>;
  categoryLookup: Record<string, RoomCategory>;
  onClose: () => void;
  onEdit?: (room: Room) => void;
  // Minimize-to-bar support
  tabId?: string;
  moduleName?: string;
  onRestoreView?: (roomId: string) => void;
}

// ============================================================================
// READ-ONLY FIELD
// ============================================================================

const ReadOnlyField: React.FC<{ label: string; value?: string | number | boolean | null; span?: boolean }> = ({ label, value, span }) => (
  <div style={span ? { gridColumn: '1 / -1' } : undefined}>
    <label className="form-label">{label}</label>
    <div style={{ padding: '0.5rem', background: 'var(--bg-secondary)', borderRadius: '0.375rem', border: '1px solid var(--form-border-color)' }}>
      {value != null && value !== '' ? (typeof value === 'boolean' ? (value ? 'Yes' : 'No') : String(value)) : '-'}
    </div>
  </div>
);

// ============================================================================
// COMPONENT
// ============================================================================

const RoomViewModal: React.FC<RoomViewModalProps> = ({
  isOpen,
  room,
  propertyLookup,
  categoryLookup,
  onClose,
  onEdit,
  tabId,
  moduleName,
  onRestoreView,
}) => {
  const { minimize } = useModalMinimize();

  const handleMinimize = useCallback(() => {
    if (!tabId || !moduleName || !room) return;
    minimize({
      id: `${tabId}-view-modal-${room.id}`,
      title: `View - ${room.roomNumber}`,
      moduleName,
      tabId,
      onRestore: () => onRestoreView?.(room.id as string),
    });
    onClose();
  }, [tabId, moduleName, room, minimize, onClose, onRestoreView]);

  if (!isOpen || !room) return null;

  // Resolve FK display names from lookups
  const property = room.propertyId ? propertyLookup[room.propertyId] : null;
  const propertyDisplay = property ? `${property.propertyCode} - ${property.propertyName}` : '-';
  const category = room.roomCategoryId ? categoryLookup[room.roomCategoryId] : null;
  const categoryDisplay = category ? `${category.categoryCode} - ${category.categoryName}` : '-';

  return (
    <>
      <div className="modal-backdrop" />
      <div className="modal-container modal-lg" style={{ minHeight: 'auto' }}>
        <div className="modal-header">
          <h2 className="modal-title">Room Details</h2>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.375rem' }}>
            {tabId && (
              <button className="modal-header-btn" onClick={handleMinimize} title="Minimize to bar">
                <IconSystem.chevronDown size={16} />
              </button>
            )}
            <button className="modal-close" onClick={onClose}>
              <IconSystem.x size={20} />
            </button>
          </div>
        </div>

        <div className="modal-content" style={{ maxHeight: '70vh', overflowY: 'auto' }}>
          {/* Basic Information */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem' }}>
            <ReadOnlyField label="Room Number" value={room.roomNumber} />
            <ReadOnlyField label="Floor Number" value={room.floorNumber} />
          </div>

          {/* Property & Room Category */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem', marginTop: '1rem' }}>
            <ReadOnlyField label="Property" value={propertyDisplay} />
            <ReadOnlyField label="Room Category" value={categoryDisplay} />
          </div>

          {/* Room Status & Active Status */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem', marginTop: '1rem' }}>
            <ReadOnlyField label="Room Status" value={room.roomStatus} />
            <div>
              <label className="form-label">Status</label>
              <div>
                <span className={`badge badge-${room.isActive ? 'active' : 'inactive'}`}>
                  {room.isActive ? 'Active' : 'Inactive'}
                </span>
              </div>
            </div>
          </div>
        </div>

        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose}>
            Close
          </button>
          {onEdit && (
            <button className="btn btn-primary" onClick={() => {
              onClose();
              onEdit(room);
            }}>
              <IconSystem.edit size={16} />
              Edit
            </button>
          )}
        </div>
      </div>
    </>
  );
};

export default RoomViewModal;
